import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { normalizeUiMessage } from '../../utils/message-normalizer';
import { ModalController, LoadingController, ToastController } from '@ionic/angular';
import { OrderSlotService } from 'src/app/services/order-slot.service';
import { DatePipe } from '@angular/common';
import moment from 'moment';

@Component({
  selector: 'app-schedule-popup',
  templateUrl: './schedulePopup.html',
  styleUrls: ['./schedulePopup.scss'],
})
export class schedulePopup implements OnInit {
  @Input() mode: 'default' | 'reservation' = 'default'; // Add this line
  @Input() initialDate: string;
  @ViewChild('reservationSlotSection', { static: false }) reservationSlotSection?: ElementRef<HTMLElement>;
  @ViewChild('defaultSlotSection', { static: false }) defaultSlotSection?: ElementRef<HTMLElement>;
  
  currentStep = 1;
  selectedDate: string | undefined;
  selectedMinDate: string;
  selectedSlot: any = null;
  flattenedSlots: any[] = [];
  loadingSlots = false;
  isLocationRed: boolean = false;
  // reservation-mode slot state
  reservationSlots: any[] = [];
  selectedReservationSlot: any = null;
  loadingReservationSlots = false;
  
  restaurantCurrentDate: string;
  restaurantCurrentTime: string;
  
  // Calendar properties
  currentMonthYear: string = '';
  calendarDays: any[] = [];
  dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  private toYMD(value: any): string {
    if (!value) {
      return '';
    }

    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }

    const m = moment(raw);
    if (m.isValid()) {
      return m.format('YYYY-MM-DD');
    }

    return raw.split('T')[0] || raw;
  }

  private normalizeName(value: any): string {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[-_/]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private isPickupGroupName(name: any): boolean {
    const normalized = this.normalizeName(name);
    return normalized.includes('pick up') || normalized.includes('pickup') || normalized.includes('takeout');
  }

  private isDeliveryGroupName(name: any): boolean {
    const normalized = this.normalizeName(name);
    return normalized.includes('delivery');
  }

  private hasSourcePacketId(source: any): boolean {
    return !!source?.orderSouceDisplayPackets?.id;
  }

  private getPreferredOrderMode(source?: any): 'pickup' | 'delivery' | null {
    if (this.isPickupGroupName(source?.name)) {
      return 'pickup';
    }
    if (this.isDeliveryGroupName(source?.name)) {
      return 'delivery';
    }

    const mode = this.normalizeName(localStorage.getItem("selectedOptionsFromHome"));
    if (mode.includes('pickup') || mode.includes('takeout')) {
      return 'pickup';
    }
    if (mode.includes('delivery')) {
      return 'delivery';
    }
    return null;
  }

  private getFallbackOrderSourceIds(mode: 'pickup' | 'delivery' | null): number[] {
    if (mode === 'pickup') {
      return [4, 5, 6];
    }
    if (mode === 'delivery') {
      return [8, 9, 10];
    }
    return [4, 5, 6, 8, 9, 10];
  }

  private buildFallbackOrderSource(id: number, mode: 'pickup' | 'delivery' | null): any {
    const name = mode === 'delivery' ? 'Online Delivery' : 'Online Takeout';
    return {
      name,
      orderSouceDisplayPackets: { id }
    };
  }

  private parseStoredJson(raw: any): any {
    if (!raw || raw === 'null') {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  private persistResolvedOrderSource(source: any): void {
    if (!source) {
      return;
    }
    localStorage.setItem("selectedOrderSourceGroupObject", JSON.stringify(source));
    localStorage.setItem("selectedOrderSourceGroup", JSON.stringify(source));
  }

  private resolveOrderSourceForSlots(): any {
    const selectedObject = this.parseStoredJson(localStorage.getItem("selectedOrderSourceGroupObject"));
    if (this.hasSourcePacketId(selectedObject)) {
      return selectedObject;
    }

    const selectedGroup = this.parseStoredJson(localStorage.getItem("selectedOrderSourceGroup"));
    if (this.hasSourcePacketId(selectedGroup)) {
      return selectedGroup;
    }

    const allGroups = this.parseStoredJson(localStorage.getItem("getAllOrderSouceGroupDisplay")) || [];
    const allData = this.parseStoredJson(localStorage.getItem("getAllDataByBusinessId")) || {};
    const functions = Array.isArray(allData?.locationsToFunctions) ? allData.locationsToFunctions : [];
    const activePickupFunction = functions.find((fn: any) => {
      const name = this.normalizeName(fn?.name);
      return (name.includes('pickup') || name.includes('pick up') || name.includes('takeout')) &&
        String(fn?.status || '').toUpperCase() === 'A' &&
        !!fn?.id;
    });
    const activeDeliveryFunction = functions.find((fn: any) => {
      const name = this.normalizeName(fn?.name);
      return name.includes('delivery') &&
        String(fn?.status || '').toUpperCase() === 'A' &&
        !!fn?.id;
    });
    const pickupEnabled = functions.some((fn: any) => {
      const name = this.normalizeName(fn?.name);
      return (name.includes('pickup') || name.includes('pick up') || name.includes('takeout')) &&
        String(fn?.status || '').toUpperCase() === 'A';
    });
    const deliveryEnabled = functions.some((fn: any) => this.normalizeName(fn?.name).includes('delivery') && String(fn?.status || '').toUpperCase() === 'A');

    const mode = this.normalizeName(localStorage.getItem("selectedOptionsFromHome"));
    const preferPickup = mode.includes('pickup') || mode.includes('takeout');
    const preferDelivery = mode.includes('delivery');

    const pickupGroup = allGroups.find((group: any) => this.hasSourcePacketId(group) && this.isPickupGroupName(group?.name) && (pickupEnabled || !deliveryEnabled));
    const deliveryGroup = allGroups.find((group: any) => this.hasSourcePacketId(group) && this.isDeliveryGroupName(group?.name) && (deliveryEnabled || !pickupEnabled));

    if (preferPickup && pickupGroup) {
      return pickupGroup;
    }
    if (preferDelivery && deliveryGroup) {
      return deliveryGroup;
    }
    if (pickupGroup) {
      return pickupGroup;
    }
    if (deliveryGroup) {
      return deliveryGroup;
    }

    if (preferPickup && activePickupFunction) {
      return {
        name: activePickupFunction.name || 'Online Takeout',
        orderSouceDisplayPackets: { id: activePickupFunction.id },
        __sourceFallbackType: 'function'
      };
    }
    if (preferDelivery && activeDeliveryFunction) {
      return {
        name: activeDeliveryFunction.name || 'Online Delivery',
        orderSouceDisplayPackets: { id: activeDeliveryFunction.id },
        __sourceFallbackType: 'function'
      };
    }
    if (activePickupFunction) {
      return {
        name: activePickupFunction.name || 'Online Takeout',
        orderSouceDisplayPackets: { id: activePickupFunction.id },
        __sourceFallbackType: 'function'
      };
    }
    if (activeDeliveryFunction) {
      return {
        name: activeDeliveryFunction.name || 'Online Delivery',
        orderSouceDisplayPackets: { id: activeDeliveryFunction.id },
        __sourceFallbackType: 'function'
      };
    }

    return allGroups.find((group: any) => this.hasSourcePacketId(group)) || null;
  }

  constructor(
    private modalCtrl: ModalController,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    public datePipe: DatePipe,
    private orderSlot: OrderSlotService
  ) {
    // Get the timezone offset to calculate the correct current restaurant date
    const timeZone = JSON.parse(localStorage.getItem('timezone') || '{}');
    let now = moment();
    
    if (timeZone && timeZone.timezoneName) {
      // Adjust current time to restaurant's timezone
      now = moment().utcOffset(timeZone.timezoneName);
    }

    // ALWAYS use the actual current date for min date validation
    this.restaurantCurrentDate = now.format('YYYY-MM-DD');
    this.restaurantCurrentTime = now.format('HH:mm:ss');
    
    console.log('Using REAL restaurant current date:', this.restaurantCurrentDate);
    
    // Set the selected date to the restaurant's current date by default
    this.selectedDate = this.restaurantCurrentDate;
    
    // Set minimum date to restaurant's current date (TODAY)
    this.selectedMinDate = this.restaurantCurrentDate;
  }

  ngOnInit() {
    const business = JSON.parse(localStorage.getItem('selectedBusiness'));
    this.isLocationRed = business?.locationId === 940;
    console.log('Popup initialized with selected date:', this.selectedDate);
    console.log('Mode:', this.mode);
    
    // If an initial date was passed (editing), use it, otherwise keep today
    if (this.initialDate) {
        this.selectedDate = this.toYMD(this.initialDate);
    }
    
    this.generateCalendar();
    if (this.selectedDate) {
      this.onDateSelected(undefined, false);
    }
  }

  generateCalendar() {
    if (!this.selectedDate) return;
    
    const [year, month] = this.selectedDate.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    
    this.currentMonthYear = moment(this.selectedDate).format('MMMM YYYY');
    
    // Get days from previous month to fill the first week
    const startOffset = firstDay.getDay(); // 0 = Sunday
    const daysFromPrevMonth = startOffset;
    
    // Get days from next month to fill the last week
    const endOffset = 6 - lastDay.getDay();
    
    this.calendarDays = [];
    
    // Previous month days
    const prevMonth = new Date(year, month - 2, 1);
    const prevMonthLastDay = new Date(year, month - 1, 0).getDate();
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const dateStr = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      this.calendarDays.push({
        day: day,
        date: dateStr,
        isCurrentMonth: false,
        isRestaurantToday: dateStr === this.restaurantCurrentDate,
        isPast: dateStr < this.restaurantCurrentDate
      });
    }
    
    // Current month days
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      this.calendarDays.push({
        day: day,
        date: dateStr,
        isCurrentMonth: true,
        isRestaurantToday: dateStr === this.restaurantCurrentDate,
        isPast: dateStr < this.restaurantCurrentDate
      });
    }
    
    // Next month days
    const nextMonth = new Date(year, month, 1);
    for (let day = 1; day <= endOffset; day++) {
      const dateStr = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      this.calendarDays.push({
        day: day,
        date: dateStr,
        isCurrentMonth: false,
        isRestaurantToday: dateStr === this.restaurantCurrentDate,
        isPast: dateStr < this.restaurantCurrentDate
      });
    }
  }

  previousMonth() {
    if (!this.selectedDate) return;
    const newDate = moment(this.selectedDate).subtract(1, 'month').format('YYYY-MM-DD');
    this.selectedDate = newDate;
    this.generateCalendar();
  }

  nextMonth() {
    if (!this.selectedDate) return;
    const newDate = moment(this.selectedDate).add(1, 'month').format('YYYY-MM-DD');
    this.selectedDate = newDate;
    this.generateCalendar();
  }

  selectCustomDate(day: any) {
    if (day.isPast) {
      this.presentToast('Cannot select past dates');
      return;
    }
    
    // Update selected date (this should be in YYYY-MM-DD format)
    this.selectedDate = day.date;
    
    // Regenerate calendar to update highlighting
    this.generateCalendar();
    
    // Call onDateSelected to log the selection
    this.onDateSelected(undefined, true);
    
    // If in reservation mode, we don't auto-dismiss anymore
    // Let user click the Confirm Date button
  }

  getDayStyle(day: any): any {
    const style: any = {
      'aspect-ratio': '1',
      'display': 'flex',
      'align-items': 'center',
      'justify-content': 'center',
      'border-radius': '50%',
      'cursor': day.isPast ? 'not-allowed' : 'pointer',
      'transition': 'all 0.2s',
      'opacity': day.isCurrentMonth ? '1' : '0.3'
    };
    
    if (day.isRestaurantToday) {
      // Only highlight the restaurant's today (Feb 15) in blue
      style['background'] = '#007bff';
      style['color'] = 'white';
      style['font-weight'] = 'bold';
    } else if (day.isPast) {
      style['color'] = '#ccc';
      style['background'] = '#f5f5f5';
    } else {
      style['color'] = '#333';
    }
    
    return style;
  }

  formatDateForDisplay(dateStr: string): string {
    if (!dateStr) return '';

    const normalizedDate = this.toYMD(dateStr);
    const date = moment(normalizedDate, 'YYYY-MM-DD');
    if (!date.isValid()) {
      return normalizedDate;
    }

    return this.datePipe.transform(date.toDate(), 'fullDate') || normalizedDate;
  }

  close() {
    this.modalCtrl.dismiss();
  }

  async onDateSelected(event?: any, shouldFocusSlots: boolean = true) {
    const rawDate = event?.detail?.value ?? this.selectedDate;
    this.selectedDate = this.toYMD(rawDate);
    if (!this.selectedDate) return;
    console.log('User selected date:', this.selectedDate);
    if (this.mode === 'reservation') {
      // Load reservation slots for the selected date
      this.selectedReservationSlot = null;
      this.reservationSlots = [];
      await this.loadReservationSlotsForDate();
      if (shouldFocusSlots) {
        this.focusSlotsSection();
      }
      return;
    }
    // Auto-load slots for the newly selected date (default mode)
    this.selectedSlot = null;
    this.flattenedSlots = [];
    await this.loadSlotsForDate();
    if (shouldFocusSlots) {
      this.focusSlotsSection();
    }
  }

  private focusSlotsSection() {
    setTimeout(() => {
      const section =
        this.mode === 'reservation'
          ? this.reservationSlotSection?.nativeElement
          : this.defaultSlotSection?.nativeElement;

      if (!section) {
        return;
      }

      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      section.focus({ preventScroll: true });
    }, 120);
  }

async loadReservationSlotsForDate() {
    this.selectedDate = this.toYMD(this.selectedDate);
    if (!this.selectedDate) return;
    const locationInfo = JSON.parse(localStorage.getItem('locationInfo') || '{}');
    if (!locationInfo?.id) return;
    this.loadingReservationSlots = true;

    const isToday = this.selectedDate === this.restaurantCurrentDate;

    try {
      const res: any = await this.orderSlot
        .getReservationSlotForDateTimeAndLocationIdForWebsite(locationInfo.id, this.selectedDate, '00:00:00')
        .toPromise();

      let slots = (res || []).map((slot: any) => {
        const d = new Date().setHours(
          +slot.time.split(':')[0],
          +slot.time.split(':')[1],
          +slot.time.split(':')[2]
        );
        return {
          ...slot,
          slotTimeDisplay: new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit' }).format(d),
          date: this.selectedDate
        };
      });

      // Filter out past slots if selected date is today
      if (isToday) {
        slots = slots.filter((slot: any) => slot.time > this.restaurantCurrentTime);
      }

      this.reservationSlots = slots;
    } catch (err) {
      console.error('Failed to load reservation slots', err);
      this.reservationSlots = [];
    } finally {
      this.loadingReservationSlots = false;
    }
  }

  selectReservationSlot(slot: any) {
    this.selectedReservationSlot = this.selectedReservationSlot === slot ? null : slot;
  }

  async nextStep() {
    if (this.currentStep === 1) {
      if (!this.selectedDate) {
        this.presentToast('Please select a date');
        return;
      }
      
      // Validate that selected date is not before restaurant's current date
      if (this.selectedDate < this.restaurantCurrentDate) {
        this.presentToast('Cannot select past dates');
        return;
      }
      
      if (this.mode === 'reservation') {
        // For reservation mode, just dismiss with the selected date
        this.modalCtrl.dismiss({ selectedDate: this.selectedDate });
      } else {
        // For default mode, proceed to slot selection
        await this.loadSlotsForDate();
      }
    } else if (this.currentStep === 2 && this.selectedSlot) {
      this.prepareConfirmationData();
      this.currentStep = 3;
    }
  }

  // New method for reservation mode confirmation
  confirmReservationDate() {
    if (!this.selectedDate) {
      this.presentToast('Please select a date');
      return;
    }
    if (this.selectedDate < this.restaurantCurrentDate) {
      this.presentToast('Cannot select past dates');
      return;
    }
    this.modalCtrl.dismiss({
      selectedDate: this.selectedDate,
      selectedSlot: this.selectedReservationSlot || null
    });
  }

  async loadSlotsForDate() {
    this.selectedDate = this.toYMD(this.selectedDate);
    if (!this.selectedDate) return;
    
    this.loadingSlots = true;
    
    // Use the selected date
    const formattedDate = this.toYMD(this.selectedDate);
    
    // Determine what time to use for slot filtering
    let time = '00:00:00';
    if (formattedDate === this.restaurantCurrentDate) {
      // If it's today, use current restaurant time
      time = this.restaurantCurrentTime;
    }

    console.log('Loading slots for date:', formattedDate, 'with time:', time);

    try {
      await this.getShiftSlotsByOrderSourceId(formattedDate, time);
      this.currentStep = 2;
    } catch (err) {
      console.error('Slot loading error:', err);
      this.presentToast('Failed to load available slots.');
    } finally {
      this.loadingSlots = false;
    }
  }

  async getShiftSlotsByOrderSourceId(date: string, time: string): Promise<void> {
    const businessHours = JSON.parse(localStorage.getItem("businessHours") || '[]');
    const normalizedDate = this.toYMD(date);
    
    // Get day of week from the date
    const dayOfWeek = moment(normalizedDate, 'YYYY-MM-DD').day() + 1; // Convert to 1-7 where 1 is Sunday

    // Filter business hours for this day
    const openHours = businessHours.filter((h: any) => Number(h?.day?.id) === dayOfWeek && Number(h?.isClosed) === 0);
    
    if (openHours.length === 0) {
      // Do not block API call here: backend may still return slots for configured sources.
      console.warn('No open business hours found for selected day from local cache; continuing slot API call.', {
        normalizedDate,
        dayOfWeek,
        cachedHoursCount: businessHours.length
      });
    }

    const orderSource: any = this.resolveOrderSourceForSlots();
    if (!orderSource?.orderSouceDisplayPackets?.id) {
      this.flattenedSlots = [];
      this.presentToast('Unable to load schedule slots right now.');
      return;
    }
    this.persistResolvedOrderSource(orderSource);

    try {
      const primaryId = Number(orderSource.orderSouceDisplayPackets.id);
      const mode = this.getPreferredOrderMode(orderSource);
      const candidateIds: number[] = [primaryId];

      if (orderSource?.__sourceFallbackType === 'function' || primaryId >= 100) {
        for (const fallbackId of this.getFallbackOrderSourceIds(mode)) {
          if (!candidateIds.includes(fallbackId)) {
            candidateIds.push(fallbackId);
          }
        }
      }

      let displayMessage = '';

      for (const sourceId of candidateIds) {
        const response: any = await this.orderSlot.getShiftSlotsByOrderSourceId(
          sourceId,
          normalizedDate,
          time
        ).toPromise();

        if (response?.displayMessage) {
          displayMessage = response.displayMessage;
          continue;
        }

        if (!Array.isArray(response) || response.length === 0) {
          continue;
        }

        if (sourceId !== primaryId) {
          const fallbackSource = this.buildFallbackOrderSource(sourceId, mode);
          this.persistResolvedOrderSource(fallbackSource);
          console.log('Switched slot source id', { primaryId, sourceId, mode });
        }

        // Process slots
// Process slots
        this.flattenedSlots = response.map((slot: any) => {
          const startTime = moment(slot.slotTime, 'HH:mm:ss');
          const endTime = startTime.clone().add(slot.slotInterval, 'minutes');
          
          return {
            ...slot,
            slotTimeDisplay: startTime.format('hh:mm A'),
            slotEndTimeDisplay: endTime.format('hh:mm A'),
            isSelected: false,
            date: normalizedDate
          };
        });

        // Filter out past slots if selected date is today
        if (normalizedDate === this.restaurantCurrentDate) {
          this.flattenedSlots = this.flattenedSlots.filter(
            (slot: any) => slot.slotTime > this.restaurantCurrentTime
          );
        }

        console.log('Processed slots:', this.flattenedSlots);
        return;
      }

      this.flattenedSlots = [];
      if (displayMessage) {
        this.presentToast(displayMessage);
      }
    } catch (err) {
      console.error('Error loading slots:', err);
      this.flattenedSlots = [];
      throw err;
    }
  }

  selectSlot(slot: any) {
    if (slot.isBlocked) return;
    if (this.selectedSlot) this.selectedSlot.isSelected = false;
    slot.isSelected = true;
    this.selectedSlot = slot;
  }

  prepareConfirmationData() {
    if (!this.selectedSlot || !this.selectedDate) return;
    
    // Use the selectedDate which is in YYYY-MM-DD format (from the calendar)
    // This should be "2026-02-15" (restaurant date)
    const dateMoment = moment(this.selectedDate);
    
    // Store the raw date for API
    this.selectedSlot.date = this.selectedDate; // "2026-02-15"
    
    // Format for display as "DD MMM, YYYY" (e.g., "15 Feb, 2026")
    this.selectedSlot.selectedDateDisplay = dateMoment.format('DD MMM, YYYY');
    
    console.log('Selected date raw:', this.selectedDate);
    console.log('Formatted display:', this.selectedSlot.selectedDateDisplay);
  }

  goBack() {
    if (this.currentStep === 3) {
      this.currentStep = 2;
    } else if (this.currentStep === 2) {
      this.currentStep = 1;
    }
  }

  async confirmAndClose() {
    if (!this.selectedSlot) return;

    const packet = { HoldShiftSlotPacket: { shiftSlots: this.selectedSlot } };
    try {
      const holdId = await this.orderSlot.holdShiftSlotForSlotId(packet).toPromise();
      this.selectedSlot.holdedSlotId = holdId;
      this.modalCtrl.dismiss({ selectedSlot: this.selectedSlot });
    } catch (err) {
      this.presentToast('Failed to reserve slot. Please try again.');
      console.error(err);
    }
  }

  async presentToast(msg: string) {
    const toast = await this.toastCtrl.create({ 
      message: normalizeUiMessage(msg), 
      duration: 2000, 
      position: 'middle'
    });
    toast.present();
  }
}
